import { create } from "zustand";
import { persist, createJSONStorage } from "zustand/middleware";
import { useCanvasId } from "@/utils/utils";
import {
  clearSelection,
  setSelectedElementKey,
  setAllSelectedElementKeys,
} from "../iframeSlice";
import { useEffect, useRef, useMemo, useCallback } from "react";
import { Storyboard } from "@/utils/types";
import { STORYBOARD_TYPES } from "@/utils/constants";
import path from "path-browserify";
import { useIssuesStore } from "./issues-store";
import { DebouncedFunc, debounce, difference, isEqual } from "lodash-es";
import { useSupabaseStore } from "./supabaseStore";
import store from "../store";

export type PersistedStoryboardConfig = {
  id: string;
  width: number;
  height: number;
  x: number;
  y: number;
  zIndex: number;
  type: STORYBOARD_TYPES;

  // Only for application storyboards
  route?: string;

  // Only for component storyboards
  importName?: string;
  importPath?: string;
  name?: string;
};

export interface PersistedPageConfig {
  id: string;
  name: string;
  storyboards: PersistedStoryboardConfig[];

  // Not actually in the file contents but read from the file system
  folderPath: string;
  configPath: string;
  created_at: string;
  updated_at: string;
}

export type Page = Omit<PersistedPageConfig, "storyboards"> & {
  storyboards: Array<Storyboard & PersistedStoryboardConfig>;
};

type DebounceEntry<F extends (...args: any[]) => unknown> = {
  callback: F;
  debounced: DebouncedFunc<(...args: Parameters<F>) => void>;
};

type KeyedDebouncedFunction<F extends (...args: any[]) => unknown> = {
  (...args: Parameters<F>): void;
  flush: (key: string) => void;
};

function keyedDebounce<F extends (...args: any[]) => unknown>(
  callback: F,
  wait: number,
  getKey: (...args: Parameters<F>) => string,
  options?: Parameters<typeof debounce>[2],
) {
  const cache = new Map<string, DebounceEntry<F>>();

  const executor = (...args: Parameters<F>) => {
    const key = getKey(...args);
    let entry = cache.get(key);
    if (!entry) {
      const newEntry: DebounceEntry<F> = {
        callback,
        debounced: debounce(
          (...innerArgs: Parameters<F>) => {
            newEntry.callback(...innerArgs);
          },
          wait,
          options,
        ),
      };
      cache.set(key, newEntry);
      entry = newEntry;
    } else {
      entry.callback = callback;
    }
    entry.debounced(...args);
  };

  (executor as KeyedDebouncedFunction<F>).flush = (key: string) => {
    const entry = cache.get(key);
    entry?.debounced.flush();
  };

  return executor as KeyedDebouncedFunction<F>;
}

export interface PagesStore {
  // Core State
  pages: Record<string, Page[]>; // Map of canvasId to pages array
  tempobookPagesDirs: Record<string, string>; // Map of canvasId to tempobook base path

  // State Utilities
  setPages: (canvasId: string, pages: Page[]) => void;
  addPage: (canvasId: string, page: Page) => void;
  removePage: (canvasId: string, pageId: string) => void;
  updatePage: (
    canvasId: string,
    pageId: string,
    updates: Partial<Page>,
  ) => void;

  setTempobookBasePath: (
    canvasId: string,
    tempobookPagesDirPath: string,
  ) => void;

  getPageSourcePath: (canvasId: string, pageId: string) => string | undefined;
  getPageForStoryboard: (
    canvasId: string,
    storyboardId: string,
  ) => Page | undefined;

  // UI State & Utilities - now canvas-specific
  selectedPageId: Record<string, string | null>; // Map of canvasId to selected page ID
  setSelectedPageId: (canvasId: string, pageId: string | null) => void;

  // Canvas viewport state - page-specific within each canvas
  pageViewport: Record<
    string,
    Record<string, { x: number; y: number; zoom: number }>
  >; // canvasId -> pageId -> viewport
  setPageViewport: (
    canvasId: string,
    pageId: string,
    viewport: Partial<{ x: number; y: number; zoom: number }>,
  ) => void;

  // Element selection state - page-specific within each canvas
  selectedElementKeys: Record<
    string,
    Record<
      string,
      {
        selectedElementKey: string | null;
        multiSelectedElementKeys: string[] | null;
      }
    >
  >; // canvasId -> pageId -> selection state
  setSelectedElementKeys: (
    canvasId: string,
    pageId: string,
    selectedElementKey: string | null,
    multiSelectedElementKeys: string[] | null,
  ) => void;

  // Pending page activities tracking to avoid stale file updates overwriting
  // newer instant updates
  // null = pending (awaiting API response), always considered "ahead" of file updates
  // string = confirmed updated_at from backend
  pendingPageActivities: Record<string, Record<string, string | null>>;
  addPendingPageActivity: (pageId: string, activityId: string) => void;
  setPendingPageActivityUpdatedAt: (
    pageId: string,
    activityId: string,
    updatedAt: string,
  ) => void;
  clearReflectedActivities: (pageId: string, fileUpdatedAt: string) => void;
  hasPendingActivities: (pageId: string) => boolean;
}

/**
 * Transform PersistedPageConfig to Page with full Storyboard objects.
 * Computes fullFilePath by resolving importPath relative to folderPath.
 * Also converts relative paths to absolute file paths using basePathToProject.
 * Adds pageId to each storyboard for easy lookup.
 */
function transformPersistedPageToPage(
  persistedPage: PersistedPageConfig,
  basePathToProject?: string | null,
): Page {
  return {
    ...persistedPage,
    storyboards: persistedPage.storyboards
      .map((persistedStoryboard: PersistedStoryboardConfig) => {
        if (persistedStoryboard.type === "APPLICATION") {
          return {
            ...persistedStoryboard,
            type: "APPLICATION" as STORYBOARD_TYPES,
            created: true,
            created_at: persistedPage.created_at,
            pageId: persistedPage.id,
          };
        }

        if (!persistedStoryboard.importPath) {
          // Should never happen
          console.error(
            "No import path for storyboard",
            persistedStoryboard.id,
          );
          return undefined;
        }

        const pagePath = path.join(basePathToProject, persistedPage.folderPath);
        const absoluteFilePath = path.resolve(
          pagePath,
          persistedStoryboard.importPath,
        );
        const componentProps = {
          ...persistedStoryboard,
          name: persistedStoryboard.name || persistedStoryboard.importName,
          fullFilePath: absoluteFilePath,
          created: Boolean(persistedStoryboard.importPath),
          created_at: persistedPage.created_at,
          pageId: persistedPage.id,
          storyComponentName: persistedStoryboard.importName,
        };

        return componentProps as Storyboard;
      })
      .filter((sb) => sb !== undefined),
  };
}

/**
 * Update the pages store from persisted page data (from loadAllFiles response).
 * This is the primary way to update pages from file system data.
 *
 * @param canvasId - The canvas ID to update pages for
 * @param persistedPages - Array of persisted page configs from loadAllFiles
 * @param basePathToProject - Base path to the project (required for resolving file paths)
 * @param tempobookPagesDir - Optional tempobook pages directory path
 * @param providedBasePathToProject - Base path to project from loadAllFiles response.
 */
export function updateFromPersistedPages(
  canvasId: string,
  persistedPages: PersistedPageConfig[],
  providedBasePathToProject?: string,
  tempobookPagesDir?: string,
): void {
  try {
    // Use provided basePathToProject from loadAllFiles response, or fall back to Redux store
    const basePathToProject =
      providedBasePathToProject ?? store.getState().codebase.basePathToProject;

    // Transform persisted pages to full Page objects
    const transformedPages = persistedPages.map((persistedPage) =>
      transformPersistedPageToPage(persistedPage, basePathToProject),
    );

    // Ensure the selected page id is always valid
    const selectedPageId = usePagesStore.getState().selectedPageId[canvasId];
    if (
      !selectedPageId ||
      !transformedPages.find((p) => p.id === selectedPageId)
    ) {
      usePagesStore
        .getState()
        .setSelectedPageId(canvasId, transformedPages[0]?.id || null);
    }

    // Update the store
    usePagesStore.getState().setPages(canvasId, transformedPages);

    if (tempobookPagesDir) {
      usePagesStore
        .getState()
        .setTempobookBasePath(canvasId, tempobookPagesDir);
    }
  } catch (error) {
    console.error("[updateFromPersistedPages] Error updating pages:", error);
  }
}

/**
 * Update the pages store from incremental page changes (from loadAllFiles with onlyTheseFiles).
 * This merges changes rather than replacing the entire pages array.
 *
 * @param canvasId - The canvas ID to update pages for
 * @param changedPages - Array of pages that were created or updated
 * @param deletedPagePaths - Array of page config paths that were deleted
 * @param basePathToProject - Base path to the project (required for resolving file paths)
 */
export function updateFromIncrementalPageChanges(
  canvasId: string,
  changedPages: PersistedPageConfig[],
  deletedPagePaths: string[],
  basePathToProject: string,
): void {
  try {
    const pagesStore = usePagesStore.getState();
    const currentPages = pagesStore.pages[canvasId] || [];

    // Transform changed pages to full Page objects
    const transformedChangedPages = changedPages.map((persistedPage) =>
      transformPersistedPageToPage(persistedPage, basePathToProject),
    );

    // Start with current pages
    let updatedPages = [...currentPages];

    // Remove deleted pages by matching configPath
    // Do this prior to adding so that if a move happened we first delete and then add
    if (deletedPagePaths.length > 0) {
      updatedPages = updatedPages.filter((page) => {
        return !deletedPagePaths.includes(page.configPath);
      });
    }

    // Apply changed pages with pending activity check
    for (const transformedPage of transformedChangedPages) {
      // Clear activities that have been reflected in this file update
      pagesStore.clearReflectedActivities(
        transformedPage.id,
        transformedPage.updated_at,
      );

      // Only apply the file update if there are no remaining pending activities
      // This prevents "bouncing" when rapid storyboard moves happen
      if (!pagesStore.hasPendingActivities(transformedPage.id)) {
        const existingIndex = updatedPages.findIndex(
          (p) => p.id === transformedPage.id,
        );
        if (existingIndex >= 0) {
          // Update existing page
          updatedPages[existingIndex] = transformedPage;
        } else {
          // Add new page
          updatedPages.push(transformedPage);
        }
      }
      // If there ARE pending activities, skip updating this page -
      // the instant update already has a more recent state
    }

    // Update the store with merged pages
    pagesStore.setPages(canvasId, updatedPages);

    const selectedPageId = pagesStore.selectedPageId[canvasId];
    if (!selectedPageId || !updatedPages.find((p) => p.id === selectedPageId)) {
      pagesStore.setSelectedPageId(canvasId, updatedPages[0]?.id || null);
    }
  } catch (error) {
    console.error(
      "[updateFromIncrementalPageChanges] Error merging page changes:",
      error,
    );
  }
}

// Store creation function that can be reused with custom initial state
const createPagesStore = (initialState?: {
  pages?: Record<string, Page[]>;
  tempobookPagesDirs?: Record<string, string>;
  selectedPageId?: Record<string, string | null>;
  pageViewport?: Record<
    string,
    Record<string, { x: number; y: number; zoom: number }>
  >;
  selectedElementKeys?: Record<
    string,
    Record<
      string,
      {
        selectedElementKey: string | null;
        multiSelectedElementKeys: string[] | null;
      }
    >
  >;
}) =>
  create<PagesStore>()(
    persist(
      (set, get) => ({
        pages: initialState?.pages || {},
        tempobookPagesDirs: initialState?.tempobookPagesDirs || {},
        selectedPageId: initialState?.selectedPageId || {},
        pageViewport: initialState?.pageViewport || {},
        selectedElementKeys: initialState?.selectedElementKeys || {},

        setPages: (canvasId, pages) =>
          set((state) => ({
            pages: { ...state.pages, [canvasId]: pages },
          })),
        addPage: (canvasId, page) =>
          set((state) => {
            const existingPages = state.pages[canvasId] || [];
            const existingPageIndex = existingPages.findIndex(
              (p) => p.id === page.id,
            );

            if (existingPageIndex >= 0) {
              // Update existing page by ID
              const updatedPages = [...existingPages];
              updatedPages[existingPageIndex] = {
                ...updatedPages[existingPageIndex],
                ...page,
              };
              return {
                pages: { ...state.pages, [canvasId]: updatedPages },
              };
            } else {
              // Add new page
              return {
                pages: {
                  ...state.pages,
                  [canvasId]: [...existingPages, page],
                },
              };
            }
          }),
        removePage: (canvasId, pageId) =>
          set((state) => {
            const canvasPages = state.pages[canvasId] || [];
            const updatedPages = canvasPages.filter(
              (page) => page.id !== pageId,
            );
            return {
              pages: { ...state.pages, [canvasId]: updatedPages },
              selectedPageId: {
                ...state.selectedPageId,
                [canvasId]:
                  state.selectedPageId[canvasId] === pageId
                    ? null
                    : state.selectedPageId[canvasId],
              },
            };
          }),
        updatePage: (canvasId, pageId, updates) =>
          set((state) => {
            const canvasPages = state.pages[canvasId] || [];
            const updatedPages = canvasPages.map((page) =>
              page.id === pageId ? { ...page, ...updates } : page,
            );
            return {
              pages: { ...state.pages, [canvasId]: updatedPages },
            };
          }),
        setSelectedPageId: (canvasId, pageId) =>
          set((state) => {
            // Just update the selected page ID
            // PagesSideEffects will handle saving/restoring viewport and selection
            return {
              selectedPageId: { ...state.selectedPageId, [canvasId]: pageId },
            };
          }),
        setTempobookBasePath: (canvasId, tempobookPagesDirPath) =>
          set((state) => ({
            tempobookPagesDirs: {
              ...state.tempobookPagesDirs,
              [canvasId]: tempobookPagesDirPath,
            },
          })),

        setPageViewport: (canvasId, pageId, viewport) =>
          set((state) => {
            return {
              pageViewport: {
                ...state.pageViewport,
                [canvasId]: {
                  ...(state.pageViewport[canvasId] || {}),
                  [pageId]: {
                    ...(state.pageViewport[canvasId]?.[pageId] || {
                      x: 0,
                      y: 0,
                      zoom: 1,
                    }),
                    ...viewport,
                  },
                },
              },
            };
          }),

        setSelectedElementKeys: (
          canvasId,
          pageId,
          selectedElementKey,
          multiSelectedElementKeys,
        ) =>
          set((state) => ({
            selectedElementKeys: {
              ...state.selectedElementKeys,
              [canvasId]: {
                ...(state.selectedElementKeys[canvasId] || {}),
                [pageId]: {
                  selectedElementKey,
                  multiSelectedElementKeys,
                },
              },
            },
          })),

        getPageSourcePath: (canvasId, pageId) => {
          const pages = get().pages[canvasId];
          const page = pages?.find((p) => p.id === pageId);
          return page?.configPath;
        },
        getPageForStoryboard: (canvasId, storyboardId) => {
          const pages = get().pages[canvasId];
          if (!pages) return undefined;
          return pages.find((page) =>
            page.storyboards.some((sb) => sb.id === storyboardId),
          );
        },

        // Pending page activities tracking
        pendingPageActivities: {},

        addPendingPageActivity: (pageId, activityId) =>
          set((state) => ({
            pendingPageActivities: {
              ...state.pendingPageActivities,
              [pageId]: {
                ...(state.pendingPageActivities[pageId] || {}),
                [activityId]: null, // null = pending API response
              },
            },
          })),

        setPendingPageActivityUpdatedAt: (pageId, activityId, updatedAt) =>
          set((state) => {
            const pageActivities = state.pendingPageActivities[pageId];
            if (!pageActivities || !(activityId in pageActivities))
              return state;

            return {
              pendingPageActivities: {
                ...state.pendingPageActivities,
                [pageId]: {
                  ...pageActivities,
                  [activityId]: updatedAt,
                },
              },
            };
          }),

        clearReflectedActivities: (pageId, fileUpdatedAt) =>
          set((state) => {
            const pageActivities = state.pendingPageActivities[pageId];
            if (!pageActivities) return state;

            const remaining: Record<string, string | null> = {};
            for (const [actId, actUpdatedAt] of Object.entries(
              pageActivities,
            )) {
              // Keep if: null (still pending API) OR actUpdatedAt > fileUpdatedAt
              if (actUpdatedAt === null || actUpdatedAt > fileUpdatedAt) {
                remaining[actId] = actUpdatedAt;
              }
            }

            return {
              pendingPageActivities: {
                ...state.pendingPageActivities,
                [pageId]: remaining,
              },
            };
          }),

        hasPendingActivities: (pageId) => {
          const pageActivities = get().pendingPageActivities[pageId];
          return pageActivities && Object.keys(pageActivities).length > 0;
        },
      }),
      {
        name: "pages-store-viewport",
        storage: createJSONStorage(() => localStorage),
        partialize: (state) => ({
          pages: state.pages,
          selectedPageId: state.selectedPageId,
          tempobookPagesDirs: state.tempobookPagesDirs,
        }),
      },
    ),
  );

// Default global store
export const usePagesStore = createPagesStore();

// Export the store creation function for use in providers
export { createPagesStore };

export const getStoryboardsForPage = (page: Page): Storyboard[] => {
  return page.storyboards || [];
};

// Hook to get storyboards for a specific page
export const useStoryboardsForPage = (
  canvasId: string | undefined,
  pageId: string | null | undefined,
): Storyboard[] => {
  const pages = usePagesStore((state) => state.pages);

  return useMemo(() => {
    if (!canvasId || !pageId) return [];

    const canvasPages = pages[canvasId];
    if (!canvasPages) return [];

    const page = canvasPages.find((p) => p.id === pageId);
    if (!page?.storyboards) return [];

    // Storyboards are already transformed to Storyboard[] type
    return page.storyboards;
  }, [canvasId, pageId, pages]);
};

/**
 * Hook to sync all pages for the current canvas to Supabase pages table
 *
 * Behavior:
 * - Tracks all pages in the current canvas (pages[canvasId])
 * - For new pages: INSERT with created_at from page.created_at
 * - For existing pages: UPDATE all fields + updated_at
 * - For deleted pages: UPDATE deleted_at to now()
 * - Uses per-page debouncing to batch rapid changes
 */
function useSyncToPagesTable() {
  const canvasId = useCanvasId();
  const allCanvasPages = usePagesStore((state) => state.pages[canvasId] || []);

  // Cache of what's currently in Supabase (by pageId)
  // Structure: { pageId: { ...pageData, _existsInDB: boolean } }
  const supabasePagesCache = useRef<Record<string, Page>>({});

  // Memoize the current pages as a lookup map
  const currentPagesMap = useMemo(() => {
    const map = new Map<string, Page>();
    allCanvasPages.forEach((page) => map.set(page.id, page));
    return map;
  }, [allCanvasPages]);

  /**
   * Sync a single page to Supabase
   * - Determines if page needs INSERT (new) or UPDATE (existing)
   * - Converts Page to database-compatible format (storyboards as JSONB)
   */
  const syncPageToSupabase = useCallback(
    async (page: Page) => {
      const supabaseClient = useSupabaseStore.getState().supabaseClient;
      if (!supabaseClient) {
        console.warn("[PagesStore] Supabase client not available");
        return;
      }

      try {
        const pageData = {
          id: page.id,
          canvas_id: canvasId,
          name: page.name,
          storyboards: page.storyboards, // JSONB field
          created_at: page.created_at,
          updated_at: page.updated_at,
          deleted_at: null,
        };

        // Use upsert to handle both INSERT and UPDATE
        const { error } = await supabaseClient.from("pages").upsert(pageData, {
          onConflict: "id",
          ignoreDuplicates: false,
        });

        if (error) {
          console.error("[PagesStore] Error upserting page:", error);
          return;
        }

        console.debug(
          "[PagesStore] Synced page to Supabase:",
          page.id,
          pageData,
        );
        supabasePagesCache.current[page.id] = { ...page };
      } catch (error) {
        console.error("[PagesStore] Error syncing page:", error);
      }
    },
    [canvasId],
  );

  /**
   * Mark a page as deleted in Supabase (soft delete)
   */
  const softDeletePage = useCallback(async (pageId: string) => {
    const supabaseClient = useSupabaseStore.getState().supabaseClient;
    if (!supabaseClient) return;

    try {
      const { error } = await supabaseClient
        .from("pages")
        .update({ deleted_at: new Date().toISOString() })
        .eq("id", pageId);

      if (error) {
        console.error("[PagesStore] Error soft-deleting page:", error);
        return;
      }

      console.debug("[PagesStore] Soft-deleted page:", pageId);
      delete supabasePagesCache.current[pageId];
    } catch (error) {
      console.error("[PagesStore] Error soft-deleting page:", error);
    }
  }, []);

  const STORYBOARDS_SAVE_DEBOUNCE_MS = 1 * 1000;

  // Debounced sync and delete functions
  const debouncedSyncPage = useMemo(
    () =>
      keyedDebounce(
        syncPageToSupabase,
        STORYBOARDS_SAVE_DEBOUNCE_MS,
        (page) => page.id,
      ),
    [syncPageToSupabase],
  );

  const debouncedSoftDelete = useMemo(
    () =>
      keyedDebounce(
        softDeletePage,
        STORYBOARDS_SAVE_DEBOUNCE_MS,
        (pageId) => pageId,
      ),
    [softDeletePage],
  );

  // Main sync effect
  useEffect(() => {
    if (!canvasId) return;

    // Sync all current pages
    allCanvasPages.forEach((page) => {
      const cachedPage = supabasePagesCache.current[page.id];

      // Only sync if page has changed
      if (!isEqual(page, cachedPage)) {
        debouncedSyncPage(page);
      }
    });

    // Find deleted pages (in cache but not in current pages)
    Object.keys(supabasePagesCache.current).forEach((pageId) => {
      if (!currentPagesMap.has(pageId)) {
        debouncedSoftDelete(pageId);
      }
    });
  }, [
    canvasId,
    allCanvasPages,
    currentPagesMap,
    debouncedSyncPage,
    debouncedSoftDelete,
  ]);
}

export const PagesSideEffects = () => {
  // Sync page storyboards changes to Supabase
  useSyncToPagesTable();
  return null;
};
